import { AbstractControl, AsyncValidatorFn, FormControl, ValidationErrors, ValidatorFn } from '@angular/forms'
import { PhoneNumberType, PhoneNumberUtil } from 'google-libphonenumber'
import { map, Observable, of, startWith, take, switchMap, debounceTime, catchError, timer, tap } from 'rxjs'
import Color from 'color'

const validsPhoneTypes = [PhoneNumberType.MOBILE, PhoneNumberType.FIXED_LINE_OR_MOBILE]

/**
 * Custom validators for Angular forms.
 * This class provides various static methods to create validators for different scenarios.
 * These validators can be used to validate form controls in Angular applications.
 * @example
 * import { GbValidators } from 'path-to-gb-validators';
 * const myValidator = GbValidators.mustMatch('password', 'confirmPassword');
 */
export class GbValidators {
  /**
   * this custom validator is used to check the password and confirm password fields
   * it should set the form as valid once those 2 fields values are the same.
   * @param password form field
   * @param confirmPassword form field
   * @returns ValidatorFn
   */
  static mustMatch(password: string, confirmPassword: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const _password = control.get(password)?.value
      const _confirmPassword = control.get(confirmPassword)?.value

      if (_password !== _confirmPassword) {
        control.get(confirmPassword)?.setErrors({ noMatch: true })
        return { noMatch: true }
      }
      if (_password.length > 0 && _confirmPassword.length > 0 && _password === _confirmPassword) {
        control.get(confirmPassword)?.setErrors(null)
      }

      return null
    }
  }

  /**
   * Checks if the control value matches the provided email(s) and sets error if true.
   */
  static match(emails: string[] | null): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value || !emails?.length) {
        return null
      }

      const matches = emails.some((e) => control.value.toLowerCase() === e.toLowerCase())
      return matches ? { match: true } : null
    }
  }

  static dateAfter(startDate: string, endDate: string, message?: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const _startDate = control.get(startDate)?.value
      const _endDate = control.get(endDate)?.value

      if (!_startDate || !_endDate) {
        return null
      }

      if (new Date(_startDate) >= new Date(_endDate)) {
        const error = {
          dateAfter: { message: message || 'FORMS.ERROR.DATE_AFTER', startDate: _startDate, endDate: _endDate },
        }
        control.get(endDate)?.setErrors(error)
        control.get(endDate)?.markAsTouched()
        return error
      }

      control.get(endDate)?.setErrors(null)
      return null
    }
  }

  /**
   * Checks if max value is greater than min value
   * @param min
   * @param max
   * @returns ValidatorFn
   */
  static greaterThan(min: string, max: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const _min = control.get(min)?.value
      const _max = control.get(max)?.value

      if (!_min || !_max) {
        return null
      }

      if (Number(_max) < Number(_min)) {
        control.get(max)?.setErrors({ greaterThan: _min })
        return { greaterThan: _min }
      }

      control.get(max)?.setErrors(null)
      return null
    }
  }

  /**
   * Creates a validator function that checks if one form control value is less than or equal to another.
   * @param maxValueField - The form control name containing the maximum allowed value
   * @param fieldToValidate - The form control name containing the value to validate
   * @returns A validator function that returns null if valid, or an error object if the min value exceeds max value
   */
  static lessThanOrEqualTo(maxValueField: string, fieldToValidate: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const maxValue = control.get(maxValueField)?.value
      const minValue = control.get(fieldToValidate)?.value

      if (!maxValue || !minValue) {
        return null
      }

      if (Number(minValue) > Number(maxValue)) {
        control.get(fieldToValidate)?.setErrors({ lessThanOrEqualTo: maxValue })
        return { lessThanOrEqualTo: maxValue }
      }

      control.get(fieldToValidate)?.setErrors(null)
      return null
    }
  }

  /**
   * this custom validator checks if the the phone number form input is valid
   * @param supportedCountryCodes - Optional array of supported country dial codes (e.g., ['1', '44', '91'])
   * @returns ValidatorFn
   */
  static mobileNumber(supportedCountryCodes?: string[]): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const phone = control.value

      if (!phone) {
        return null
      }
      const phoneUtil = PhoneNumberUtil.getInstance()
      let parsed
      try {
        parsed = phoneUtil.parse(phone)
      } catch (e) {
        return { validPhoneNumber: true }
      }

      // Check if country code is supported (if supportedCountryCodes is provided)
      if (supportedCountryCodes && supportedCountryCodes.length > 0) {
        const countryCode = parsed.getCountryCode()?.toString()
        if (countryCode && !supportedCountryCodes.includes(countryCode)) {
          return { unsupportedCountryCode: true }
        }
      }

      if (phoneUtil.isValidNumber(parsed) && validsPhoneTypes.includes(phoneUtil.getNumberType(parsed))) {
        control.setErrors(null)
        return null
      } else {
        return { validPhoneNumber: true }
      }
    }
  }

  /**
   * @deprecated there is no point using this validator if the new gb-form-input component
   * you can just use the directive numbersOnly instead
   * @returns
   */
  static wholeNumber(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null
      }

      const value = Number(control.value)
      return Number.isInteger(value) && value >= 0 ? null : { wholeNumber: true }
    }
  }

  static upperLowerCase(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null
      }

      const hasUpperCase = /[A-Z]/.test(control.value)
      const hasLowerCase = /[a-z]/.test(control.value)

      return hasUpperCase && hasLowerCase ? null : { upperLowerCase: true }
    }
  }

  static alphanumericOnly(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null
      }

      const regex = /^[a-zA-Z0-9]+$/
      return regex.test(control.value) ? null : { alphanumericOnly: true }
    }
  }

  static auctionUrlName(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null
      }
      const regex = /^[A-Za-z0-9-_]+$/
      const valid = regex.test(control.value)

      return valid ? null : { invalidCharacters: true }
    }
  }

  static lessThanFormControl(totalUnitsAvailableFormControl: FormControl, unitName: string): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      return totalUnitsAvailableFormControl.valueChanges.pipe(
        startWith(totalUnitsAvailableFormControl.value || 0),
        take(1),
        map((totalAvailableValue: number) => {
          return control.value <= totalAvailableValue
            ? null
            : { totalUnitsExceeded: totalAvailableValue, unitName: unitName || 'FORMS.ERROR.UNITS' }
        }),
      )
    }
  }

  static greaterThanOrEqualToFormControl(controlToCompare: FormControl): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      return controlToCompare.valueChanges.pipe(
        startWith(controlToCompare.value || 0),
        take(1),
        map((value: number) => {
          if (!control.value || !controlToCompare.value) {
            return null
          }

          return control.value >= value ? null : { greaterThanOrEqualTo: controlToCompare.value }
        }),
      )
    }
  }

  static lessThanOrEqualToFormControl(controlToCompare: FormControl): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      return controlToCompare.valueChanges.pipe(
        startWith(controlToCompare.value || 0),
        take(1),
        map((value: number) => {
          if (!control.value || !controlToCompare.value) {
            return null
          }

          return control.value <= value ? null : { lessThanOrEqualTo: controlToCompare.value }
        }),
      )
    }
  }

  static paddleNumberAvailableValidator(
    dispatchCheck: (value: string) => Observable<boolean>,
    initialValue: string | null | undefined,
  ): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      const value = control.value
      if (!value || value === initialValue) return of(null)

      return of(value).pipe(
        debounceTime(300),
        switchMap((val) =>
          dispatchCheck(val).pipe(
            map((isAvailable) => (isAvailable ? null : { paddleNumberInUse: true })),
            catchError(() => of({ paddleNumberInUse: true })),
          ),
        ),
      )
    }
  }

  static discountCodeUniqueValidator(
    checkExists: (code: string) => Observable<boolean>,
    initialValue: string | null | undefined,
  ): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      const value = control.value?.trim()
      if (!value || value === initialValue) return of(null)

      return timer(400).pipe(
        switchMap(() =>
          checkExists(value).pipe(
            tap((exists) => {
              if (exists) control.markAsTouched()
            }),
            map((exists) => (exists ? { codeAlreadyExists: true } : null)),
            catchError(() => of(null)),
          ),
        ),
      )
    }
  }

  static validColorValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const color = control.value

      if (!color) {
        return null
      }

      try {
        Color(color)
        return null
      } catch (e) {
        return { invalidColor: true }
      }
    }
  }

  /**
   * Check if the text content length exceeds the specified maximum length.
   * Strips out HTML tags and counts only the text content.
   */
  static maxTextLengthValidator(maxLength: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null
      }

      const textContent = control.value.replace(/<[^>]*>/g, '').trim()
      return textContent.length > maxLength
        ? { maxTextLength: { requiredLength: maxLength, actualLength: control.value } }
        : null
    }
  }

  /**
   * function that check if the option is selected
   * @returns ValidatorFunction
   */
  static controlValueIsObject(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const controlValue = control.value
      if (controlValue && typeof controlValue === 'object') {
        return null
      }
      return { noOptionSelected: true }
    }
  }

  /**
   * Custom validator to ensure both start and end date fields are provided together.
   * Returns an error if only one of the two fields has a value.
   * @param startDateField - The form control name for the start date
   * @param endDateField - The form control name for the end date
   * @returns ValidatorFn that validates both dates are filled together or both are empty
   */
  static dateRangeComplete(startDateField: string, endDateField: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const startDate = control.get(startDateField)?.value
      const endDate = control.get(endDateField)?.value

      // If one date is filled but not the other, return validation error
      if ((startDate && !endDate) || (!startDate && endDate)) {
        return { dateRangeIncomplete: true }
      }

      return null
    }
  }

  /**
   * Custom validator to make a field required based on another form control's value.
   * @param conditionControlName - The form control name to check for the condition
   * @param conditionValue - The value or predicate function that triggers the required validation.
   *                         Can be:
   *                         - A specific value to match (e.g., true, 'active')
   *                         - A predicate function that receives the control value and returns boolean
   *                         - true (default) to check if control has any truthy value
   * @param fieldToValidate - The form control name that should be required when condition is met
   * @returns ValidatorFn that validates the field is required when condition is true
   * @example
   * // Make field required when another field has any value
   * GbValidators.requiredWhen('end_date', true, 'start_date')
   *
   * // Make field required when another field equals specific value
   * GbValidators.requiredWhen('status', 'active', 'reason')
   *
   * // Make field required when another field is greater than 10
   * GbValidators.requiredWhen('quantity', (value) => value > 10, 'bulk_discount')
   *
   * // Make field required when another field is less than or equal to 5
   * GbValidators.requiredWhen('stock', (value) => value <= 5, 'reorder_note')
   */
  static requiredWhen(
    conditionControlName: string,
    conditionValue: any | ((value: any) => boolean) = true,
    fieldToValidate: string,
  ): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const conditionControl = control.get(conditionControlName)
      const fieldControl = control.get(fieldToValidate)

      if (!conditionControl || !fieldControl) {
        return null
      }

      let conditionMet: boolean

      // If conditionValue is a function, use it as a predicate
      if (typeof conditionValue === 'function') {
        conditionMet = conditionValue(conditionControl.value)
      }
      // If conditionValue is true, check if condition control has any truthy value
      else if (conditionValue === true) {
        conditionMet = !!conditionControl.value
      }
      // Otherwise, check for exact match
      else {
        conditionMet = conditionControl.value === conditionValue
      }

      const fieldValue = fieldControl.value

      if (conditionMet && (!fieldValue || (typeof fieldValue === 'string' && !fieldValue.trim()))) {
        fieldControl.setErrors({ required: true })
        return { required: true }
      }

      if (!conditionMet && fieldControl.hasError('required')) {
        fieldControl.setErrors(null)
      }

      return null
    }
  }
}
